import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Brain, CheckCircle, AlertCircle, Clock, Play, Pause } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend } from 'recharts';

const PublicCognitiveAssessment = () => {
  const { token } = useParams();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(60 * 60);
  const [timerRunning, setTimerRunning] = useState(false);

  const questionsPerPage = 10;
  const currentPage = Math.floor(currentQuestionIndex / questionsPerPage);
  const progress = ((currentQuestionIndex + 1) / 60) * 100;

  useEffect(() => {
    loadLinkData();
  }, [token]);

  useEffect(() => {
    let interval;
    if (timerRunning && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerRunning(false);
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning, timeRemaining]);

  const loadLinkData = async () => {
    try {
      const linkResponse = await axios.get(`${backendUrl}/api/cognitive/public/${token}`);
      
      if (linkResponse.data.completed) {
        setError('Esta avaliação já foi completada.');
        setLoading(false);
        return;
      }
      
      setLinkData(linkResponse.data);
      
      const questionsResponse = await axios.get(`${backendUrl}/api/cognitive/public-questions`);
      setQuestions(questionsResponse.data.questions);
      
      const initialAnswers = {};
      questionsResponse.data.questions.forEach(q => {
        initialAnswers[q.id] = null;
      });
      setAnswers(initialAnswers);
      
      setLoading(false);
      setTimerRunning(true);
    } catch (error) {
      console.error('Error loading link:', error);
      if (error.response?.status === 404) {
        setError('Link não encontrado ou inválido.');
      } else if (error.response?.status === 403) {
        setError(error.response.data.detail || 'Link expirado ou inativo.');
      } else {
        setError('Erro ao carregar informações da avaliação.');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setTimerRunning(false);
      
      const answersArray = Object.keys(answers)
        .filter(qId => answers[qId] !== null)
        .map(questionId => ({
          question_id: parseInt(questionId),
          selected_option: answers[questionId]
        }));

      const totalTimeMinutes = Math.ceil((60 * 60 - timeRemaining) / 60);

      const response = await axios.post(
        `${backendUrl}/api/cognitive/public/${token}/complete`,
        { 
          answers: answersArray,
          time_minutes: totalTimeMinutes
        }
      );

      setResult(response.data);
      setCompleted(true);
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setError(error.response?.data?.detail || 'Erro ao enviar respostas. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getICGColor = (icg) => {
    if (icg >= 125) return 'text-purple-600';
    if (icg >= 105) return 'text-blue-600';
    if (icg >= 89) return 'text-green-600';
    if (icg >= 73) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryName = (key) => {
    const names = {
      raciocinio_logico: 'Raciocínio Lógico',
      raciocinio_numerico: 'Raciocínio Numérico',
      raciocinio_abstrato: 'Raciocínio Abstrato',
      resolucao_problemas: 'Resolução de Problemas',
      velocidade_processamento: 'Velocidade'
    };
    return names[key] || key;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando avaliação...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Erro</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed && result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-4xl w-full border-2 border-purple-200">
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-3xl font-bold text-gray-800 mb-2">
              Avaliação Concluída!
            </CardTitle>
            <p className="text-gray-600">Obrigado por completar a Avaliação Cognitiva GridProfile</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Índice Cognitivo GridProfile (ICG)</p>
              <p className={`text-6xl font-bold ${getICGColor(result.icg)}`}>
                {result.icg}
              </p>
              <p className="text-xl font-semibold text-gray-700 mt-2">
                {result.classificacao}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Score Geral: {result.score_geral.toFixed(1)}%
              </p>
            </div>

            {result.scores && (
              <div className="bg-white rounded-lg p-6 border-2 border-gray-100">
                <h3 className="text-lg font-semibold mb-4 text-center">Seu Perfil Cognitivo</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart data={Object.entries(result.scores).map(([key, value]) => ({
                    categoria: getCategoryName(key),
                    valor: parseFloat(value.toFixed(1))
                  }))}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis 
                      dataKey="categoria" 
                      tick={{ fill: '#374151', fontSize: 11, fontWeight: 600 }}
                    />
                    <PolarRadiusAxis 
                      angle={90} 
                      domain={[0, 100]} 
                      tick={{ fill: '#6b7280', fontSize: 9 }}
                    />
                    <Radar
                      name="Score (%)"
                      dataKey="valor"
                      stroke="#7c3aed"
                      fill="#7c3aed"
                      fillOpacity={0.5}
                      strokeWidth={2}
                    />
                    <Legend formatter={() => 'Pontuação (%)'} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Este resultado é baseado em suas respostas ao questionário. 
                O ICG é uma ferramenta de triagem, não um teste psicológico validado. 
                Seus resultados foram enviados para a empresa.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const allAnswered = Object.values(answers).every(a => a !== null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Brain className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-800">
                    Avaliação Cognitiva GridProfile
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Questão {currentQuestionIndex + 1} de {questions.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                <span className={`text-lg font-mono font-bold ${
                  timeRemaining < 300 ? 'text-red-600' : 'text-purple-600'
                }`}>
                  {formatTime(timeRemaining)}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setTimerRunning(!timerRunning)}
                >
                  {timerRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {linkData && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-purple-900">
                  <strong>Participante:</strong> {linkData.participant_name}
                </p>
                <p className="text-sm text-purple-900">
                  <strong>Email:</strong> {linkData.participant_email}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progresso</span>
                <span>{currentQuestionIndex + 1} de {questions.length}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {currentQuestions.map((question, idx) => {
              const globalIndex = currentPage * questionsPerPage + idx;
              if (globalIndex !== currentQuestionIndex) return null;
              
              return (
                <div key={question.id} className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-lg font-medium text-gray-800">
                      {question.question}
                    </p>
                  </div>

                  <div className="space-y-2">
                    {Object.entries(question.options).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => setAnswers({...answers, [question.id]: key})}
                        className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                          answers[question.id] === key
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <span className="font-semibold text-purple-600 mr-3">{key.toUpperCase()})</span>
                        {value}
                      </button>
                    ))}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentQuestionIndex === 0}
                      variant="outline"
                    >
                      ← Anterior
                    </Button>

                    {currentQuestionIndex < questions.length - 1 ? (
                      <Button
                        onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Próxima →
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={!allAnswered || submitting}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {submitting ? 'Enviando...' : 'Concluir Avaliação'}
                      </Button>
                    )}
                  </div>

                  {!allAnswered && currentQuestionIndex === questions.length - 1 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <AlertCircle className="inline h-4 w-4 mr-2" />
                      <span className="text-sm text-yellow-800">
                        Responda todas as questões antes de concluir
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicCognitiveAssessment;
